import { createHttpService } from 'src/apis/ApiService';
import { TStudentDetails } from 'src/apis/students/students.interfaces';
import { TUserDetails } from 'src/apis/users/users.interfaces';
import { mockStudentDetails } from 'src/__mocks__/students/studentDetailsMock';
import { IBankDetails } from 'src/apis/staff/staffPayout.interfaces';
import isAppWithMock from 'src/utils/isAppWithMock';

export type GuardianProfileData = {
  student: TStudentDetails;
  guardians: TUserDetails[];
  extraGuardians: TUserDetails[];
};

const useGuardianProfileService = () => {
  const httpAPI = createHttpService();
  const baseGuardianPath = '/guardian/profile';
  const withMockData = isAppWithMock();

  const getProfileData = (studentId: string): Promise<GuardianProfileData> => {
    if (withMockData) {
      const studentData = mockStudentDetails;
      const guardians = studentData.guardianDetails || [];
      
      // Separate primary guardians (father/mother) from extra guardians
      const primaryGuardians = guardians.slice(0, 2);
      const extraGuardians = guardians.slice(2);
      
      return Promise.resolve({
        student: studentData,
        guardians: primaryGuardians,
        extraGuardians,
      });
    }

    return httpAPI
      .get<GuardianProfileData>(`${baseGuardianPath}/${studentId}`)
      .then((response) => response)
      .catch((error) => {
        console.error('Error fetching guardian profile data:', error);
        throw error;
      });
  };

  const updateBankDetails = (studentId: string, bankDetails: IBankDetails): Promise<void> => {
    if (withMockData) {
      // In mock mode, simulate successful update
      return Promise.resolve();
    }

    return httpAPI
      .put<void>(`${baseGuardianPath}/${studentId}/bank-details`, bankDetails)
      .then(() => {
        // Success - mutation will handle cache invalidation
      })
      .catch((error) => {
        console.error('Error updating bank details:', error);
        throw error;
      });
  };

  return {
    getProfileData,
    updateBankDetails,
  };
};

export default useGuardianProfileService;
